import { Stack, styled } from '@mui/material';
import { V2_FIELD_HEIGHT } from '@onevision-resources/design-theme';

// Fits the wider of AND and OR with the field's chevron, so switching moves no row.
const COMBINATOR_COLUMN_WIDTH = '5.25rem';
const HANDLE_COLUMN_WIDTH = '1rem';
const REMOVE_COLUMN_WIDTH = '1.75rem';
// Below this a row stops fitting attribute, operator and value side by side.
const STACKED_MAX_WIDTH = '35rem';
// The widest a row gets: attribute, operator and value at their widths on a 1280px page.
const RULE_MAX_WIDTH = '50.25rem';
const GROUP_PADDING = 1.75;
const GROUP_BORDER_WIDTH = '1px';

/**
 * Every row of every group shares one set of columns: AND / OR, handle, attribute, operator,
 * value, remove. Each group is its own size container, so a group too narrow for one line
 * stacks its own conditions whether it sits on a page, in a dialog or on a phone.
 *
 * On a wide page a row stops at its widest and the builder at the widest row plus what each
 * level of groups costs (its AND / OR column and its box), so nesting widens the builder
 * instead of crushing the fields.
 *
 * Selectors carry `.ruleGroup` twice to outrank react-querybuilder's stylesheet, which the
 * screens import for its drag-and-drop indicators.
 */
export const ConditionsBuilderRoot = styled(Stack, {
  name: 'ConditionsBuilder',
  shouldForwardProp: (prop) => prop !== 'groupLevel',
})<{ groupLevel: number }>(({ theme, groupLevel }) => {
  const { sys } = theme.palette.custom.OV;
  const fieldHeight = theme.typography.pxToRem(V2_FIELD_HEIGHT.small);
  const combinatorColumn = `${COMBINATOR_COLUMN_WIDTH} + ${theme.spacing(1)}`;
  const groupIndent = `${combinatorColumn} + 2 * ${theme.spacing(GROUP_PADDING)} + 2 * ${GROUP_BORDER_WIDTH}`;

  return {
    containerType: 'inline-size',
    minWidth: 0,
    maxWidth: `calc(${combinatorColumn} + ${RULE_MAX_WIDTH} + ${groupLevel} * (${groupIndent}))`,

    '& .queryBuilder': { minWidth: 0 },

    '& .ruleGroup': {
      display: 'flex',
      flexDirection: 'column-reverse',
      gap: theme.spacing(1),
      minWidth: 0,
      padding: 0,
      border: 'none',
      background: 'none',
    },

    '& .ruleGroup:not([data-level="0"])': {
      containerType: 'inline-size',
      padding: theme.spacing(GROUP_PADDING),
      border: `${GROUP_BORDER_WIDTH} solid ${sys.outlineVariant}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: sys.surfaceContainerHigh,
    },

    '& .ruleGroup .ruleGroup-header': {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(0.25),

      '& > .queryBuilder-dragHandle': {
        marginRight: theme.spacing(0.75),
        color: sys.onSurfaceVariant,
        cursor: 'grab',
      },
    },

    '& .ruleGroup .ruleGroup-body': {
      display: 'grid',
      gridTemplateColumns: `${COMBINATOR_COLUMN_WIDTH} minmax(0, 1fr)`,
      gap: theme.spacing(1),
      // AND / OR and WHERE face a row's first line, also when the row stacks
      alignItems: 'start',
      marginLeft: 0,

      '&:empty': { display: 'none' },

      '&:has(> .rule, > .ruleGroup)::before': {
        content: '"Where"',
        gridColumn: 1,
        display: 'flex',
        alignItems: 'center',
        minHeight: fieldHeight,
        fontSize: theme.typography.pxToRem(12),
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: sys.onSurfaceVariant,
      },

      '& > .betweenRules': { gridColumn: 1 },
      '& > .rule, & > .ruleGroup': { gridColumn: 2, minWidth: 0 },
    },

    '& .ruleGroup .rule': {
      display: 'grid',
      // a value taller than a field (a multi-line text) grows down from the first line, so the
      // other controls stay level with the row's WHERE / AND / OR
      gridTemplateAreas: '"handle field operator value remove" ". . . value ."',
      gridTemplateColumns: `${HANDLE_COLUMN_WIDTH} minmax(0, 1.2fr) minmax(0, 0.9fr) minmax(0, 1.1fr) ${REMOVE_COLUMN_WIDTH}`,
      gridTemplateRows: `${fieldHeight} auto`,
      gap: theme.spacing(0, 1),
      alignItems: 'center',
      minWidth: 0,
      maxWidth: RULE_MAX_WIDTH,

      '& > .queryBuilder-dragHandle': {
        gridArea: 'handle',
        display: 'flex',
        justifyContent: 'center',
        color: sys.onSurfaceVariant,
        cursor: 'grab',
      },
      '& > .rule-fields': { gridArea: 'field' },
      '& > .rule-operators': { gridArea: 'operator' },
      '& > .rule-value': { gridArea: 'value' },
      '& > .rule-remove': { gridArea: 'remove', justifySelf: 'center' },
    },

    '& .dndOver': { borderColor: sys.primary },

    [`@container (max-width: ${STACKED_MAX_WIDTH})`]: {
      '& .ruleGroup .rule': {
        gridTemplateAreas: '"field remove" "operator ." "value ."',
        gridTemplateColumns: `minmax(0, 1fr) ${REMOVE_COLUMN_WIDTH}`,
        gridTemplateRows: 'none',
        rowGap: theme.spacing(0.75),

        '& > .queryBuilder-dragHandle': { display: 'none' },
      },
      // a group's AND / OR goes above its box, so nesting costs only the box's padding
      '& .ruleGroup .ruleGroup-body > .ruleGroup': { gridColumn: '1 / -1' },
    },
  };
});
